export interface B2BLanguageSwitchConfig {
  currentLanguage: string;
  options: B2BLanguageSwitchOption[];
}

export interface B2BLanguageSwitchOption {
  href: string;
  label: string;
  menuLabel: string;
  value: string;
}

export interface B2BLanguageItem {
  languageCode?: string;
  languageDisplayLabel?: string;
  languageDropDownLabel?: string;
  redirectionUrl?: string;
  vanityPath?: string;
}

const B2B_LANGUAGE_EXCLUDED_PATHS = new Set(["/"]);

const LANGUAGE_CODE_TO_ROUTE_SEGMENT: Record<string, string> = {
  en: "en",
  hi: "hindi",
  te: "telugu",
  ta: "tamil",
};
const KNOWN_LANGUAGE_ROUTE_SEGMENTS = new Set(
  [
    ...Object.values(LANGUAGE_CODE_TO_ROUTE_SEGMENT),
    ...Object.keys(LANGUAGE_CODE_TO_ROUTE_SEGMENT),
  ].map((value) => value.toLowerCase())
);

function normalizePath(pathname: string): string {
  if (!pathname) return "/";
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

function formatLanguageLabel(value: string): string {
  if (!value) return "English";
  return value
    .split("-")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function toRouteSegment(item: B2BLanguageItem): string | null {
  if (item.vanityPath) {
    const routeSegment = item.vanityPath.split("/").find(Boolean);
    if (routeSegment) return routeSegment.toLowerCase();
  }

  const code = item.languageCode?.toLowerCase();
  if (!code) return null;
  return LANGUAGE_CODE_TO_ROUTE_SEGMENT[code] ?? code;
}

function getCurrentLanguage(pathname: string): string {
  const normalizedPath = normalizePath(pathname);
  const [, firstSegment = ""] = normalizedPath.split("/");
  const normalizedSegment = firstSegment.toLowerCase();
  if (KNOWN_LANGUAGE_ROUTE_SEGMENTS.has(normalizedSegment)) {
    return normalizedSegment;
  }
  return "en";
}

function resolveCurrentPath(pathname: string, supportedRoutes: Set<string>) {
  const normalizedPath = normalizePath(pathname);
  const [, firstSegment = "", ...remainingSegments] = normalizedPath.split("/");
  const normalizedSegment = firstSegment.toLowerCase();
  const hasLanguagePrefix =
    (KNOWN_LANGUAGE_ROUTE_SEGMENTS.has(normalizedSegment) ||
      supportedRoutes.has(normalizedSegment)) &&
    normalizedSegment !== "en";
  const englishPath = hasLanguagePrefix
    ? normalizePath(`/${remainingSegments.join("/")}`)
    : normalizedPath;

  return {
    currentLanguage: getCurrentLanguage(pathname),
    englishPath,
  };
}

export function getB2BLanguageSwitchConfig(
  pathname: string,
  languageItems: B2BLanguageItem[] = [],
  search = ""
): B2BLanguageSwitchConfig | null {
  let optionsSource = languageItems
    .map((item) => {
      const value = toRouteSegment(item);
      if (!value) return null;

      const label =
        item.languageDisplayLabel ?? item.languageDropDownLabel?.split(" - ")[0] ?? value;
      const menuLabel = item.languageDropDownLabel ?? label;

      return { value, label, menuLabel };
    })
    .filter((item): item is { value: string; label: string; menuLabel: string } => item !== null);

  if (optionsSource.length === 0) {
    const normalizedPath = normalizePath(pathname);
    const [, firstSegment = "en"] = normalizedPath.split("/");
    const currentLanguage = firstSegment.toLowerCase() || "en";
    const fallbackValues = new Set<string>(["en"]);
    if (currentLanguage !== "en") {
      fallbackValues.add(currentLanguage);
    }
    optionsSource = [...fallbackValues].map((value) => {
      const label = formatLanguageLabel(value);
      return { value, label, menuLabel: `${label} - ${value.toUpperCase()}` };
    });
  }

  const supportedRoutes = new Set(optionsSource.map(({ value }) => value).filter(Boolean));
  const { currentLanguage, englishPath } = resolveCurrentPath(pathname, supportedRoutes);

  // Enable language switch by default for all content pages.
  // Only explicitly excluded paths are hidden.
  if (B2B_LANGUAGE_EXCLUDED_PATHS.has(englishPath)) {
    return null;
  }

  return {
    currentLanguage,
    options: optionsSource.map(({ label, menuLabel, value }) => ({
      label,
      menuLabel,
      value,
      href: value === "en" ? `${englishPath}${search}` : `/${value}${englishPath}${search}`,
    })),
  };
}
